import {
  Loader2,
  Building2,
  FolderOpen,
  FileText,
  Users,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Calendar,
  Trash2,
  User,
  CreditCard,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import type { WorkspaceSummary, UserSummary } from './SuperPanel';

interface WorkspacesListViewProps {
  workspaces: WorkspaceSummary[];
  users: UserSummary[];
  filteredUsers: UserSummary[];
  isLoadingUsers: boolean;
  hideTestUsers: boolean;
  setHideTestUsers: (value: boolean) => void;
  usersSectionOpen: boolean;
  setUsersSectionOpen: (value: boolean) => void;
  workspacesSectionOpen: boolean;
  setWorkspacesSectionOpen: (value: boolean) => void;
  expandedUserIds: Set<string>;
  toggleUserExpanded: (userId: string) => void;
  onSelectUser: (user: UserSummary) => void;
  onWorkspaceClick: (workspace: WorkspaceSummary) => void;
  formatDate: (dateString: string) => string;
}

export function WorkspacesListView({
  workspaces,
  users,
  filteredUsers,
  isLoadingUsers,
  hideTestUsers,
  setHideTestUsers,
  usersSectionOpen,
  setUsersSectionOpen,
  workspacesSectionOpen,
  setWorkspacesSectionOpen,
  expandedUserIds,
  toggleUserExpanded,
  onSelectUser,
  onWorkspaceClick,
  formatDate,
}: WorkspacesListViewProps) {
  return (
    <div className="space-y-6">
      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card
          className="cursor-pointer hover:shadow-lg transition-all duration-200 hover:scale-[1.02]"
          onClick={() => {
            setWorkspacesSectionOpen(true);
            setUsersSectionOpen(false);
          }}
        >
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-100 rounded-lg">
                <Building2 className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{workspaces.length}</p>
                <p className="text-sm text-gray-500">Total Workspaces</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-green-100 rounded-lg">
                <FolderOpen className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {workspaces.reduce((sum, ws) => sum + ws.project_count, 0)}
                </p>
                <p className="text-sm text-gray-500">Total Projects</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-purple-100 rounded-lg">
                <FileText className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {workspaces.reduce((sum, ws) => sum + ws.document_count, 0)}
                </p>
                <p className="text-sm text-gray-500">Total Documents</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card
          className="cursor-pointer hover:shadow-lg transition-all duration-200 hover:scale-[1.02]"
          onClick={() => {
            setUsersSectionOpen(true);
            setWorkspacesSectionOpen(false);
          }}
        >
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-orange-100 rounded-lg">
                <Users className="h-6 w-6 text-orange-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{users.length}</p>
                <p className="text-sm text-gray-500">Total Users</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* All Users Section (Collapsible) */}
      <div className="border rounded-lg bg-white">
        <div className="w-full px-6 py-4 flex items-center justify-between">
          <button
            className="flex items-center gap-2 hover:opacity-70 transition-opacity"
            onClick={() => setUsersSectionOpen(!usersSectionOpen)}
          >
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <Users className="h-5 w-5 text-orange-600" />
              All Users ({filteredUsers.length})
            </h2>
            {usersSectionOpen ? (
              <ChevronUp className="h-5 w-5 text-gray-400" />
            ) : (
              <ChevronDown className="h-5 w-5 text-gray-400" />
            )}
          </button>
          <label
            className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer"
            onClick={(e) => e.stopPropagation()}
          >
            <Checkbox
              checked={hideTestUsers}
              onCheckedChange={(checked) => setHideTestUsers(checked === true)}
            />
            Hide test users
          </label>
        </div>
        {usersSectionOpen && (
          <div className="px-6 pb-6">
            {isLoadingUsers ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-ingesta-blue-600" />
              </div>
            ) : filteredUsers.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                {hideTestUsers && users.length > 0 ? 'No non-test users found' : 'No users found'}
              </div>
            ) : (
              <div className="space-y-2">
                {filteredUsers.map((user) => (
                  <div key={user.id} className="border rounded-lg">
                    <div
                      className="px-4 py-3 flex items-center gap-4 cursor-pointer hover:bg-gray-50"
                      onClick={() => toggleUserExpanded(user.id)}
                    >
                      <div className="p-2 bg-gray-100 rounded-full">
                        <User className="h-4 w-4 text-gray-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-medium truncate">{user.email}</p>
                          {user.subscription && (
                            <span className={`px-2 py-0.5 rounded text-xs ${
                              user.subscription.status === 'active' || user.subscription.status === 'trialing'
                                ? 'bg-green-100 text-green-700'
                                : user.subscription.status === 'past_due'
                                ? 'bg-yellow-100 text-yellow-700'
                                : 'bg-gray-100 text-gray-600'
                            }`}>
                              {user.subscription.tier} ({user.subscription.status})
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-4 text-xs text-gray-400 mt-1">
                          <span className="flex items-center gap-1">
                            <Building2 className="h-3 w-3" />
                            {user.workspace_count} workspace{user.workspace_count !== 1 ? 's' : ''}
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            Created {formatDate(user.created_at)}
                          </span>
                          {user.subscription?.has_stripe && (
                            <span className="flex items-center gap-1">
                              <CreditCard className="h-3 w-3" />
                              Stripe
                            </span>
                          )}
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={(e) => {
                          e.stopPropagation();
                          onSelectUser(user);
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                      {expandedUserIds.has(user.id) ? (
                        <ChevronUp className="h-4 w-4 text-gray-400" />
                      ) : (
                        <ChevronDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                    {expandedUserIds.has(user.id) && user.workspaces.length > 0 && (
                      <div className="px-4 pb-3 pt-1 border-t bg-gray-50">
                        <p className="text-xs text-gray-500 mb-2">Workspaces:</p>
                        <div className="flex flex-wrap gap-2">
                          {user.workspaces.map((ws) => (
                            <span
                              key={ws.id}
                              className={`px-2 py-1 rounded text-xs ${
                                ws.role === 'admin'
                                  ? 'bg-purple-100 text-purple-700'
                                  : 'bg-blue-100 text-blue-700'
                              }`}
                            >
                              {ws.name} ({ws.role})
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* All Workspaces Section (Collapsible) */}
      <div className="border rounded-lg bg-white">
        <button
          className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
          onClick={() => setWorkspacesSectionOpen(!workspacesSectionOpen)}
        >
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Building2 className="h-5 w-5 text-blue-600" />
            All Workspaces ({workspaces.length})
          </h2>
          {workspacesSectionOpen ? (
            <ChevronUp className="h-5 w-5 text-gray-400" />
          ) : (
            <ChevronDown className="h-5 w-5 text-gray-400" />
          )}
        </button>
        {workspacesSectionOpen && (
          <div className="px-6 pb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {workspaces.map((workspace) => (
                <Card
                  key={workspace.id}
                  className="cursor-pointer hover:shadow-lg transition-all duration-200 hover:scale-[1.02]"
                  onClick={() => onWorkspaceClick(workspace)}
                >
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg flex items-center justify-between">
                      <span className="truncate">{workspace.name}</span>
                      <ChevronRight className="h-5 w-5 text-gray-400 shrink-0" />
                    </CardTitle>
                    <p className="text-xs text-gray-500 truncate">{workspace.owner_email}</p>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div className="flex items-center gap-2">
                        <FolderOpen className="h-4 w-4 text-gray-400" />
                        <span>{workspace.project_count} projects</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-gray-400" />
                        <span>{workspace.document_count} docs</span>
                      </div>
                    </div>
                    <p className="text-xs text-gray-400 mt-3">
                      Created {formatDate(workspace.created_at)}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
